import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import { Button, Modal, Table, Tabs, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../components/Spinner/Spinner';
import base from '../../apis';
import { formatNumber } from '../../utils/functions';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '20px',
  },
}));

const headers = {
  us_agent: ['Agent', 'Signup Bonus ($)', 'Active Account Payments ($)', 'Total ($)'],
  international: ['Agent', 'Signup Bonus ($)', 'Active Account Payments + Appointment Commission ($)', 'Total ($)'],
};

function CompensationReport() {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState('us_agent');
  const [isLoading, setIsLoading] = useState(false);
  const [elements, setElements] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleDownload = () => {
    if (elements.length === 0) {
      toast.warn('No data to download');
      return;
    }

    const text = `${headers[activeTab].join(',')}\n${elements
      .map((item) => {
        return `${item.name},${item.signup},${item.active_payments},${item.total}`;
      })
      .join('\n')}`;
    const blob = new Blob([text], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}_compensation_report_${new Date().toLocaleDateString()}.csv`;
    link.click();
  };

  const getComps = useCallback(() => {
    setIsLoading(true);
    setElements([]);
    base
      .get(`compensations/report-view/?marketer_category=${activeTab}`)
      .then(({ data }) => {
        const newData = data.map((item) => ({
          name: `${item.user.first_name} ${item.user.last_name}`,
          signup: formatNumber(item.first_commision || 0),
          active_payments: formatNumber(item.monthly_commision || 0),
          total: formatNumber(item.first_commision + item.monthly_commision || 0),
        }));
        setElements(newData);
      })
      .catch((err) => {
        toast.error('Could not get data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activeTab]);

  useEffect(() => {
    getComps();
  }, [getComps]);

  const changeTab = (val) => {
    setActiveTab(val);
    navigate('/compensation-report?selection=' + val);
  };

  const markAsPaid = () => {
    setIsLoading(true);
    base
      .post(`compensations/redraw-all-marketers-money/`, {
        marketer_category: activeTab,
      })
      .then(({ data }) => {
        toast.success('Amount reset successfully');
        setModalOpen(false);
      })
      .catch((err) => {
        toast.error('Something went wrong');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      {isLoading && <Spinner />}
      <Modal opened={modalOpen} title="Payment Confirmation" onClose={() => setModalOpen(false)}>
        Are you sure you want to mark as paid? This will reset{' '}
        <b>{activeTab === 'us_agent' ? 'US' : 'International'} agent's</b> amounts to 0.
        <Box display="flex" justifyContent="flex-end" gap="10px" ml="auto" mt={2}>
          <Button variant="outline" color="red" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="outline" color="var(--primary-color)" onClick={markAsPaid}>
            Confirm
          </Button>
        </Box>
      </Modal>
      <Box className={classes.root}>
        <Box display="flex" justifyContent="space-between">
          <Text weight={700}>Compensation Reports</Text>
          <Box display="flex" gap="10px">
            <Button variant="outline" color="red" onClick={() => setModalOpen(true)}>
              Mark as Paid
            </Button>
            <Button variant="outline" color="var(--primary-color)" onClick={handleDownload}>
              Download
            </Button>
          </Box>
        </Box>
        <Tabs value={activeTab} onTabChange={changeTab}>
          <Tabs.List>
            <Tabs.Tab value="us_agent">US Agents</Tabs.Tab>
            <Tabs.Tab value="international">International Agents</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <Table mt="sm">
          <thead style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
            <tr style={{ color: 'inherit' }}>
              {headers[activeTab].map((header) => (
                <th key={header} style={{ color: 'inherit' }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {elements.map((element) => (
              <tr key={element.name}>
                <td>{element.name}</td>
                <td>{element.signup}</td>
                <td>{element.active_payments}</td>
                <td>{element.total}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Box>
    </>
  );
}

export default CompensationReport;
